import React from 'react';
import './ConversationItem.css';

/**
 * ConversationItem Component
 * 
 * Displays a single conversation in the sidebar.
 * Shows title, last message preview, and update time.
 * Highlights when active.
 * 
 * @param {Object} conversation - Conversation data
 * @param {string} conversation.conversation_id - Unique ID
 * @param {string} conversation.title - Conversation title
 * @param {string} conversation.last_message_preview - Last message text
 * @param {string} conversation.updated_at - ISO timestamp of last update
 * @param {number} conversation.message_count - Total messages in conversation
 * @param {boolean} isActive - Whether this is the active conversation
 * @param {Function} onSelect - Callback when conversation is selected
 */
const ConversationItem = ({
  conversation,
  isActive,
  onSelect
}) => {
  const {
    conversation_id,
    title,
    last_message_preview,
    updated_at,
    message_count
  } = conversation;

  /**
   * Format relative time (e.g., "2 days ago", "1 hour ago")
   * Handles ISO strings, Unix epoch seconds, and milliseconds
   */
  const getRelativeTime = (dateValue) => {
    try {
      let date;
      if (typeof dateValue === 'number') {
        // If the timestamp is in seconds (< 1e12), convert to milliseconds
        date = new Date(dateValue < 1e12 ? dateValue * 1000 : dateValue);
      } else {
        date = new Date(dateValue);
      }

      // Guard against invalid dates
      if (isNaN(date.getTime())) return 'unknown';

      const now = new Date();
      const seconds = Math.floor((now - date) / 1000);

      if (seconds < 0) return 'just now'; // future dates (clock skew)
      if (seconds < 60) return 'just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

      // Fallback to date format
      return date.toLocaleDateString();
    } catch {
      return 'unknown';
    }
  };

  const handleClick = () => {
    onSelect(conversation_id);
  };

  const handleKeyDown = (e) => {
    // Allow Enter and Space to activate
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={`conversation-item ${isActive ? 'active' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex="0"
      aria-pressed={isActive}
      aria-label={`${title} - ${message_count} messages`}
    >
      {/* Title */}
      <div className="conversation-title">{title}</div>

      {/* Preview Text */}
      <div className="conversation-preview">{last_message_preview}</div>

      {/* Footer: Time + Message Count */}
      <div className="conversation-footer">
        <span className="conversation-time">{getRelativeTime(updated_at)}</span>
        <span className="conversation-count">{message_count} msgs</span>
      </div>

      {/* Active Indicator */}
      {isActive && <div className="active-indicator" aria-hidden="true" />}
    </div>
  );
};

export default ConversationItem;
